import React, { useRef, useCallback, useEffect } from "react";
import { createButtonPressAnimation } from "../../../../utilities";
import { Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { openGameRules } from "../../../../State/actions";
import { selectMenuActive } from "../../../../State/selectors";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { PixiSettings } from "../../../../pixiSettings";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { soundManager } from "../../../../SoundManager";

const ButtonGameRules = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getResponsiveConfig, device, orientation, dimensions } =
    useDimensions();
  const menuActive = useSelector(selectMenuActive);

  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);
  const animationRef = useRef(null);

  // Refs to track current dimensions for animation (no re-renders)
  const dimensionsRef = useRef({ width: 0, height: 0 });
  const isInitializedRef = useRef(false);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  const texture = PIXI.Assets.get(ASSET_NAME.icon_popi_game_rules);

  // Get responsive configuration from context
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);

  // Calculate responsive button position based on device and orientation
  const buttonPosition = (() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: buttonConfig.width * 0.8,
          y: buttonConfig.height * 2.05,
        };
      } else {
        // landscape
        return {
          x: buttonConfig.width * 0.8,
          y: dimensions.height - buttonConfig.height * 1.85,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: buttonConfig.width * 0.8,
          y: buttonConfig.height * 2,
        };
      } else {
        return {
          x: buttonConfig.width * 0.8,
          y: buttonConfig.height * 2.05,
        };
      }
    } else {
      return {
        x: buttonConfig.width,
        y: dimensions.height - buttonConfig.height * 2.1,
      };
    }
  })();

  const handleClick = () => {
    soundManager.play(SOUND_NAME.button_click);

    // Add press animation before opening game rules
    if (buttonSpriteRef.current) {
      createButtonPressAnimation(buttonSpriteRef.current);
    }

    dispatch(openGameRules());
  };

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [buttonSpriteRef, brightnessFilter]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, [buttonSpriteRef]);

  // Initialize sprite dimensions on mount
  useEffect(() => {
    if (buttonSpriteRef.current && !isInitializedRef.current) {
      buttonSpriteRef.current.width = 0;
      buttonSpriteRef.current.height = 0;
      dimensionsRef.current.width = 0;
      dimensionsRef.current.height = 0;
      isInitializedRef.current = true;
    }
  }, []);

  // Width and height animation effect based on menuActive state
  useEffect(() => {
    if (!buttonSpriteRef.current || !isInitializedRef.current) return;

    // Stop any existing animation
    if (animationRef.current) {
      anime.remove(animationRef.current);
    }

    // Get current dimensions from sprite (preserve current state)
    const currentWidth = buttonSpriteRef.current.width || 0;
    const currentHeight = buttonSpriteRef.current.height || 0;

    // Update ref with current values
    dimensionsRef.current.width = currentWidth;
    dimensionsRef.current.height = currentHeight;

    if (menuActive) {
      // Animate from current size to target width and height (first to appear - no delay)
      animationRef.current = anime({
        targets: dimensionsRef.current,
        width: buttonConfig.width,
        height: buttonConfig.height,
        duration: 200,
        delay: 0,
        easing: "easeOutQuad",
        update: () => {
          // Directly update sprite dimensions without causing re-renders
          if (buttonSpriteRef.current) {
            buttonSpriteRef.current.width = dimensionsRef.current.width;
            buttonSpriteRef.current.height = dimensionsRef.current.height;
          }
        },
      });
    } else {
      // Animate from current size back to 0 (last to close - 200ms delay)
      animationRef.current = anime({
        targets: dimensionsRef.current,
        width: 0,
        height: 0,
        duration: 200,
        delay: 200,
        easing: "easeInQuad",
        update: () => {
          // Directly update sprite dimensions without causing re-renders
          if (buttonSpriteRef.current) {
            buttonSpriteRef.current.width = dimensionsRef.current.width;
            buttonSpriteRef.current.height = dimensionsRef.current.height;
          }
        },
      });
    }

    // Cleanup function to stop animation on unmount
    return () => {
      if (animationRef.current) {
        anime.remove(animationRef.current);
      }
    };
  }, [menuActive, buttonConfig.width, buttonConfig.height]);

  // Clean up hover effects on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
    };
  }, []);

  return (
    <Sprite
      ref={buttonSpriteRef}
      texture={texture}
      name="secondary_button_game_rules"
      zIndex={1}
      anchor={0.5}
      x={buttonPosition.x}
      y={buttonPosition.y}
      hitArea={new PIXI.Circle(0, 0, (buttonConfig.width / 2) * 1.8)}
      eventMode="static"
      cursor="pointer"
      onpointertap={handleClick}
      onpointerover={handlePointerOver}
      onpointerout={handlePointerOut}
      accessibleTitle={t("game_rules")}
      accessibleHint={t("game_rules")}
      resolution={2}
    />
  );
};

export default ButtonGameRules;
